<div mat-dialog-title class="flex flex-row justify-between content-center items-center">
  <h2 class="text-xl font-semibold text-gray-800">Reset your password</h2>
  <button [mat-dialog-close]="false" mat-icon-button>
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-horizontal-stepper [linear]="true" #stepper>
  <mat-step [stepControl]="userFormGroup">
    <form [formGroup]="userFormGroup" class="p-5 gap-3 flex flex-col w-full">
      <ng-template matStepLabel>Username</ng-template>
      <div class="mt-6 mb-4">
        <p class="text-lg font-medium text-center text-gray-800">
          <strong> We need to verify your identity to help you recover your password.</strong>
        </p>
        <p class="text-gray-600 text-center mt-2">Please choose how you'd like to receive the verification code.</p>
      </div>
      <mat-form-field class="w-full">
        <mat-label>Username</mat-label>
        <input
          matInput
          formControlName="username"
          placeholder="Enter your username"
          (keydown.enter)="$event.preventDefault()" />
        @if (
          userFormGroup.controls.username.invalid &&
          (userFormGroup.controls.username.dirty || userFormGroup.controls.username.touched)
          ) {
          <mat-error
            >
            @if (userFormGroup.controls.username.errors?.required || userFormGroup.controls.username.errors?.mask) {
              <span
                >
                Username is required
              </span>
            }
          </mat-error>
        }
      </mat-form-field>
      <mat-radio-group
        formControlName="contactMethod"
        (change)="onContactMethodChange($event.value)"
        class="mb-3"
        color="primary">
        <mat-radio-button value="phone" class="mr-7"
          ><span class="text-gray-600 font-medium">Text Message (SMS)</span>
          <mat-icon class="ml-2 text-gray-500">sms</mat-icon></mat-radio-button
          >
          <mat-radio-button value="email"
            ><span class="text-gray-500 font-medium">Email</span>
            <mat-icon class="ml-2 text-gray-500">email</mat-icon></mat-radio-button
            >
          </mat-radio-group>

          @if (userFormGroup.controls.contactMethod?.value === phoneType) {
            <mat-form-field class="w-full">
              <mat-label>Mobile Phone Number</mat-label>
              <input
                matInput
                formControlName="phone"
                type="tel"
                mask="(000) 000-0000"
                [dropSpecialCharacters]="false"
                (keydown.enter)="$event.preventDefault()" />
              @if (userFormGroup.controls.phone.errors?.notFound) {
                <mat-error>
                  <span
                    >Incorrect username or phone number. Please verify your information and try again or contact
                    <a [routerLink]="['contact']" class="text-blue-500 underline hover:text-blue-700" mat-dialog-close="true">
                      customer support</a
                      >.
                    </span>
                  </mat-error>
                }
                @if (userFormGroup.get('phone')?.hasError('required')) {
                  <mat-error> Phone number is required. </mat-error>
                }
                @if (userFormGroup.get('phone')?.hasError('mask')) {
                  <mat-error>
                    Enter a valid phone number. E.g. (123) 456-7890
                  </mat-error>
                }
              </mat-form-field>
            }

            @if (userFormGroup.controls.contactMethod?.value === emailType) {
              <mat-form-field class="w-full">
                <mat-label>Email</mat-label>
                <input
                  matInput
                  formControlName="email"
                  placeholder="Enter your email"
                  type="email"
                  (keydown.enter)="$event.preventDefault()" />
                @if (userFormGroup.controls.email.errors?.notFound) {
                  <mat-error>
                    <span
                      >Incorrect username or email address. Please verify your information and try again or contact
                      <a [routerLink]="['contact']" class="text-blue-500 underline hover:text-blue-700" mat-dialog-close="true">
                        customer support</a
                        >.</span
                        >
                      </mat-error>
                    }
                    @if (userFormGroup.get('email')?.hasError('required')) {
                      <mat-error> Email is required. </mat-error>
                    }
                    @if (userFormGroup.get('email')?.hasError('pattern')) {
                      <mat-error> Enter a valid email address. </mat-error>
                    }
                  </mat-form-field>
                }

                @defer (on interaction (btnSendVerificationCode)) {
                @if (isLoading) {
                  <app-loading class="my-2"></app-loading>
                } @else {
                  <app-response-viewer class="my-2" [responseData]="verificationCodeResponse()"></app-response-viewer>
                }
              }
              <div class="flex justify-end gap-3">
                <button mat-raised-button matDialogClose="true">Back to Login</button>
                <button
                  mat-raised-button
                  #btnSendVerificationCode
                  color="primary"
                  (click)="onSendVerificationCode(stepper)"
                  [disabled]="userFormGroup.invalid || isLoading || verificationCodeResponse()?.status">
                  Send Verification Code
                </button>
              </div>
            </form>
          </mat-step>

          <mat-step [stepControl]="changePasswordForm">
            <ng-template matStepLabel>Password</ng-template>
            <form novalidate [formGroup]="changePasswordForm" autocomplete="off" class="p-5 flex flex-col w-full">
              @if (passwordPolicies) {
                <mat-form-field class="w-full">
                  <mat-label>Verification Code</mat-label>
                  <input
                    matInput
                    formControlName="verificationCode"
                    mask="000000"
                    placeholder="6-digit Verification Code"
                    (keydown.enter)="$event.preventDefault()" />
                  @if (changePasswordForm.controls.verificationCode.errors?.notFound) {
                    <mat-error>
                      <span> Incorrect verification code. Please verify your code and try again. </span>
                    </mat-error>
                  }
                  @if (changePasswordForm.controls.verificationCode.errors?.required) {
                    <mat-error>
                      Verification code is required.
                    </mat-error>
                  }
                </mat-form-field>

                <div class="p-4 text-gray-600 text-sm justify-center w-full">
                  <p>
                    Your verification code is valid for <strong class="text-blue-600">10 minutes</strong>. If it expires, you
                    may request a new one.
                  </p>
                  <p>
                    Please note that you are allowed
                    <strong
              [ngClass]="{
                'text-blue-600': attempts > 0 && attempts < 5,
                'text-red-600': attempts === 5,
              }"
                      >{{ 5 - attempts }} more attempts
                    </strong>
                    to resend the verification code.
                  </p>
                </div>

                <mepco-password-strength
                  #passwordStrengthComponent
                  [min]="passwordPolicies?.minimumLength"
                  [max]="30"
                  [enableDigitRule]="passwordPolicies?.requireNumbers"
                  [enableLowerCaseLetterRule]="passwordPolicies?.requireLowercase"
                  [enableUpperCaseLetterRule]="passwordPolicies?.requireUppercase"
                  [enableSpecialCharRule]="passwordPolicies?.requireSymbols"
                  (passwordStrengthForm)="getFormValues($event)">
                </mepco-password-strength>

                @if (isLoading) {
                  <app-loading class="my-2"></app-loading>
                }
                @defer (on interaction (btnChangePassword)) {
                <app-response-viewer class="mt-2 mb-4" [responseData]="changePasswordResponse()"></app-response-viewer>
              }
              @defer (on interaction (btnResendCode)) {
              <app-response-viewer class="mt-2 mb-4" [responseData]="verificationCodeResponse()"></app-response-viewer>
            }
          } @else {
            <app-loading></app-loading>
          }

          <div class="flex justify-end gap-3">
            <button mat-raised-button matDialogClose="true">Back to Login</button>
            <button
              #btnResendCode
              mat-raised-button
              (click)="onResendCode()"
          [disabled]="
            isLoading ||
            verificationCodeResponse()?.status === maxAttemptsReached ||
            attempts === 5 ||
            changePasswordResponse()?.status === approved ||
            changePasswordResponse()?.attempts === 5 ||
            changePasswordResponse()?.status === maxCheckAttemptsReached
          ">
              Resend Code
            </button>
            <button
              #btnChangePassword
              mat-raised-button
              color="primary"
              (click)="onSendVerifyCode(stepper)"
          [disabled]="
            changePasswordForm.invalid ||
            !passwordStrengthForm().valid ||
            isLoading ||
            changePasswordResponse()?.status === approved ||
            changePasswordResponse()?.status === maxCheckAttemptsReached
          ">
              Change Password
            </button>
          </div>
        </form>
      </mat-step>

      <mat-step>
        <ng-template matStepLabel>Confirmation</ng-template>

        <div class="flex flex-col w-full items-center rounded-lg shadow-md bg-gray-50 p-8 justify-center">
          <div class="flex flex-col items-center justify-center gap-2 text-gray-700">
            <div class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-scale-in">
              <mat-icon class="text-white">check</mat-icon>
            </div>
          </div>

          <div class="text-center">
            <h2 class="text-xl font-bold text-gray-800 mt-4">Password Changed Successfully!</h2>
            <p class="text-gray-600 mt-2">Your password has been updated. You can now log in with your new password.</p>
          </div>
        </div>
        <p class="text-center text-gray-600 my-6 p-3">
          If you encounter any issues, feel free to contact
          <a [routerLink]="['contact']" class="text-blue-500 underline hover:text-blue-700" mat-dialog-close="true">
            customer support</a
            >.
          </p>
          <div class="flex justify-end p-5">
            <button mat-raised-button matDialogClose="true">Back to Login</button>
          </div>
        </mat-step>
      </mat-horizontal-stepper>
